import type {
  V3CanvasHomeFeaturedListItem,
  V3CanvasHomeFeaturedMedia,
} from '$/domains/caseV3/type';
import type { FeaturedCaseGalleryItem } from './index';

interface ResolvedFeaturedCaseCover {
  url: string;
  width?: number;
  height?: number;
}

/** 仅视频作品返回可用于卡片悬浮播放的媒体地址。 */
function resolveFeaturedCasePreviewVideo(item: V3CanvasHomeFeaturedListItem) {
  const workMedia = item.case.work_media;
  return workMedia?.type === 'video' && workMedia.url ? workMedia.url : undefined;
}

/** 将接口大整数 ID 统一转换为前端业务字符串。 */
function normalizeFeaturedId(value: string | number) {
  return String(value);
}

/** 将服务端出演 IP 快照转换为卡片头像模型，封面仅使用签名 avatar。 */
function mapFeaturedCastIps(item: V3CanvasHomeFeaturedListItem) {
  return (item.casting_ips ?? []).map((castIp) => ({
    id: String(castIp.ip_id),
    assetType: castIp.asset_type,
    roleName: castIp.role_name,
    name: castIp.name || castIp.role_name,
    summary: castIp.summary,
    coverAssetId: castIp.cover_asset_id,
    avatarUrl: castIp.avatar || undefined,
  }));
}

/** 从媒体对象中读取有效的正整数尺寸。 */
function getMediaSize(media?: V3CanvasHomeFeaturedMedia) {
  if (!media || media.width <= 0 || media.height <= 0) {
    return {};
  }
  return { width: media.width, height: media.height };
}

/** 按接口缩略图优先级选择可由图片组件展示的封面。 */
function resolveFeaturedCaseCover(item: V3CanvasHomeFeaturedListItem): ResolvedFeaturedCaseCover {
  const featuredCase = item.case;
  const coverMedia = featuredCase.cover_media;
  const workMedia = featuredCase.work_media;

  if (featuredCase.cover) {
    return { url: featuredCase.cover, ...getMediaSize(coverMedia) };
  }
  if (coverMedia?.cover) {
    return { url: coverMedia.cover, ...getMediaSize(coverMedia) };
  }
  if (coverMedia?.url) {
    return { url: coverMedia.url, ...getMediaSize(coverMedia) };
  }
  if (workMedia?.cover) {
    return { url: workMedia.cover, ...getMediaSize(workMedia) };
  }
  if (workMedia?.type === 'image' && workMedia.url) {
    return { url: workMedia.url, ...getMediaSize(workMedia) };
  }
  return { url: '' };
}

/** 将单个精选接口项适配为可在不同页面复用的卡片数据模型。 */
export function mapFeaturedCaseToGalleryItem(
  item: V3CanvasHomeFeaturedListItem,
): FeaturedCaseGalleryItem | null {
  const cover = resolveFeaturedCaseCover(item);
  if (!cover.url) {
    return null;
  }
  const castIps = mapFeaturedCastIps(item);
  const totalCastIpCount = typeof item.casting_ip_count === 'number'
    ? Math.max(item.casting_ip_count, 0)
    : castIps.length;

  return {
    featuredId: normalizeFeaturedId(item.featured_id),
    caseId: String(item.case.case_id),
    roomId: item.case.room_id || (item.case.source_type === 'room' ? item.case.source_id : undefined),
    projectId: item.case.project_id,
    placement: item.placement,
    categoryId: normalizeFeaturedId(item.category_id),
    region: item.region,
    locale: item.locale,
    title: item.case.title,
    coverUrl: cover.url,
    coverAlt: item.case.title,
    coverWidth: cover.width,
    coverHeight: cover.height,
    previewVideoUrl: resolveFeaturedCasePreviewVideo(item),
    author: {
      uid: normalizeFeaturedId(item.case.user.uid),
      name: item.case.user.screen_name,
      avatarUrl: item.case.user.avatar || undefined,
    },
    castIps,
    totalCastIpCount,
    copyAllowed: item.copy_allowed,
    workflowPublic: item.presentation?.workflow_public,
    ipDisplayMode: item.ip_display_mode,
    copyDisabledReason: item.copy_disabled_reason,
  };
}

/** 批量适配精选案例，并跳过无有效图片封面的异常卡片。 */
export function mapFeaturedCasesToGalleryItems(
  list: V3CanvasHomeFeaturedListItem[],
): FeaturedCaseGalleryItem[] {
  return list.reduce<FeaturedCaseGalleryItem[]>((result, item) => {
    const galleryItem = mapFeaturedCaseToGalleryItem(item);
    if (galleryItem) {
      result.push(galleryItem);
    }
    return result;
  }, []);
}
